import React from 'react';
import { TrackPage } from 'Tracking/track-page/TrackPage';
import { TRACK_PAGE_NAMES } from 'Tracking/constants';
import { addTranslationKeys, t } from 'Src/i18n';
import { useAppContext } from 'Context/ReactAppContext';
import { SiteContainer } from 'Components/frameworks/site-container/SiteContainer';
import { CustomerLogin } from 'Components/widgets/prebooking-form/CustomerLogin';
import { PrebookingFormContainer } from 'Components/widgets/prebooking-form/PrebookingFormContainer';
import { asWidgetProps } from 'Src/types/cast-helpers';
import type {
  PrebookingFormLineProps,
  PrebookingFormResolverProps,
} from './PrebookingFormLine.utils';
import {
  getAdultsNumberOptions,
  getCivilities,
  getFormProducts,
  getKidsNumberOptions,
  getNewsletterOptins,
  getProductOptions,
  getTravelAgencyOptions,
} from './PrebookingFormLine.utils';
type PrebookingFormProps = PrebookingFormLineProps & PrebookingFormResolverProps;
export const PrebookingFormLine = (props: PrebookingFormProps) => {
  const {
    accommodationCategories,
    bookingsCriteria,
    civilities: adultCivilities,
    config,
    dateFormat,
    error,
    legalInformations,
    login,
    mainTraveler,
    mandatoryFieldsInformation,
    pax,
    products,
    resorts,
    sendRequest,
    stay,
    success,
    travelAgencies,
    estimativePrice,
    modifyRequest,
    continueRequest,
    submitError,
  } = props;
  addTranslationKeys({
    prebookingForm: {
      login,
      mainTraveler,
      mandatoryFieldsInformation,
      pax,
      sendRequest,
      stay,
      estimativePrice,
      modifyRequest,
      continueRequest,
      submitError,
    },
  });
  const appContext = useAppContext();
  const { displayUnicodeFields: hasUnicodeFields, locale, isUserLogged: hasUser } = appContext;

  // force scroll to top on page load
  if (typeof window !== 'undefined') {
    window.onbeforeunload = function () {
      window.scrollTo(0, 0);
    };
  }

  // CXO: set seller agency and id
  const agencies = getTravelAgencyOptions(travelAgencies, locale);
  const civilities = getCivilities(adultCivilities.adult, locale);
  const hasLoginButton = !!config.account?.authenticated?.greeting;
  const formProducts = getFormProducts({
    accommodationCategories,
    bookingsCriteria,
    resorts,
    durationLabels: props.stay.durationValue,
    lineProducts: products,
  });
  const productOptions = getProductOptions(products, formProducts);
  const departureDateStart = new Date(props.departureDateStart);
  const departureDateEnd = new Date(props.departureDateEnd);
  const redirectUris = {
    error: error ? error.url : null,
    success: success ? success.url : null,
  };
  const adultsOptions = getAdultsNumberOptions(props.stay.adultsAge);
  const kidsOptions = getKidsNumberOptions(props.stay.childrenAge);
  const consentOptins = getNewsletterOptins(legalInformations.bookingProcess || []);
  const loginLabels = {
    memberSpace: t('prebookingForm.login.memberSpace'),
    memberSpaceTitle: t('prebookingForm.login.memberSpaceTitle'),
    labelCTA: t('prebookingForm.login.labelCTA'),
  };
  return (
    <SiteContainer className="Line" data-testid="prebookingForm" id="prebookingform">
      <TrackPage pageName={TRACK_PAGE_NAMES.PREBOOKING_FORM} />
      {login.displayLoginIcon && (
        <CustomerLogin hasLoginButton={hasLoginButton} hasUser={hasUser} labels={loginLabels} />
      )}
      <PrebookingFormContainer
        {...asWidgetProps<React.ComponentProps<typeof PrebookingFormContainer>>({
          adultsOptions,
          agencies,
          civilities,
          dateFormat,
          departureDateEnd,
          departureDateStart,
          hasUnicodeFields,
          hasUser,
          kidsOptions,
          locale,
          consentOptins,
          productOptions,
          products: formProducts,
          redirectUris,
        })}
      />
    </SiteContainer>
  );
};
